import { ComponentType } from 'react';
import { Factory, Inspection, GroupedInspectionItem } from '@/types/Inspection.type';
import { DynamicComponentProps } from '@/types/ModuleComponentProp.type';
import { SizeChart } from '@/types/SizeChart';
import { ControlPoint } from '@/types/ControlPoint';
import { Product } from '@/types/Product';
import { Measurement } from '@/types/Measurement';
import { InspectionDefect } from '@/types/InspectionDefect';
import * as inspectionModules from '@/features/inspection-details/components/modules';
import {
  DefectCode,
  DefectCodes,
  DefectLanguage,
  DefectSeverity,
} from '@/types/DefectCategory.type';
import i18n from '@/config/i18s';
import { Template } from '@/types/Template.type';
import { Supplier } from '@/types/Supplier';
import { ApiResponse } from '@/types/ApiResponse';
import { AppConfig, APP_CONFIG_KEY } from '@/types/AppConfig.type';
import { Division } from '@/types/Division';
import { defectLabelSearchText, selectDefectLanguage } from '@/utils/defect-display-language';

const structures = new Map<string, Template>();
const jsonModules = import.meta.glob('../data/inspection/*.json', { eager: true });
for (const path in jsonModules) {
  const content = jsonModules[path] as { default: any };
  content.default.version = content.default.version || 1;
  structures.set(
    `${content?.default?.id?.toLowerCase()}_v${content?.default?.version}`,
    content.default as Template
  );
  console.log(`Loaded ${path}:`, content.default);
}

export type InspectionModuleTypes = (typeof inspectionModules)[keyof typeof inspectionModules];
export type InspectionModuleComponentMap = Record<string, ComponentType<DynamicComponentProps>>;
export interface InspectionDateFilters {
  start: string;
  end: string;
  min: string;
  max: string;
}

export interface SizeMeasurement {
  sizeChartId: string;
  dimensionCode: string;
  dimensionDescription: string;
  targetMeasurement: number;
  keyDimensionYN: boolean;
  toleranceMinus: number;
  tolerancePlus: number;
  measurementId: string | null;
  inspectionDataId: string | null;
  measurement: string | number | null;
  sampleDescription: string | null;
  sampleNumber: number | null;
  createdOn: string | null;
  version: number;
}

const restructureSizeChartData = (
  sizeCharts: SizeChart[],
  measurements: Measurement[],
  passCount: number = 3
): { [key: string]: SizeMeasurement[] } => {
  if (!sizeCharts.length) return {};

  const unsortedResult: {
    [key: string]: (SizeMeasurement & {
      sizeOrder: number;
      dimensionOrder: number;
    })[];
  } = {};

  const measurementMap = new Map<string, Measurement[]>();
  measurements.forEach((m) => {
    const key = `${m.sizeChartId}_${m.sampleNumber}`;
    if (!measurementMap.has(key)) {
      measurementMap.set(key, []);
    }
    measurementMap.get(key)!.push(m);
  });

  sizeCharts.forEach((sc) => {
    const sizeKey = sc.productSize;
    if (!unsortedResult[sizeKey]) {
      unsortedResult[sizeKey] = [];
    }

    const sequence = sc.sequence ?? 999;
    const dimensionOrder = Math.floor(sequence / 100);
    const sizeOrder = sequence % 100;

    for (let sampleNumber = 1; sampleNumber <= passCount; sampleNumber++) {
      const key = `${sc.id}_${sampleNumber}`;
      const matchingMeasurements = measurementMap.get(key) || [];

      if (!matchingMeasurements.length) {
        unsortedResult[sizeKey].push({
          sizeChartId: sc.id,
          dimensionCode: sc.dimensionCode,
          dimensionDescription: sc.dimensionDescription,
          targetMeasurement: sc.targetMeasurement,
          keyDimensionYN: sc.keyDimensionYN,
          toleranceMinus: sc.toleranceMinus,
          tolerancePlus: sc.tolerancePlus,
          version: sc.version,
          measurementId: null,
          inspectionDataId: null,
          measurement: null,
          sampleDescription: null,
          sampleNumber,
          createdOn: null,
          sizeOrder,
          dimensionOrder,
        });
      } else {
        matchingMeasurements.forEach((mappedMeasurement) => {
          const exists = unsortedResult[sizeKey].some(
            (e) =>
              e.sizeChartId === sc.id &&
              e.sampleNumber === sampleNumber &&
              e.measurementId === mappedMeasurement.id
          );

          if (!exists) {
            unsortedResult[sizeKey].push({
              sizeChartId: sc.id,
              dimensionCode: sc.dimensionCode,
              dimensionDescription: sc.dimensionDescription,
              targetMeasurement: sc.targetMeasurement,
              keyDimensionYN: sc.keyDimensionYN,
              toleranceMinus: sc.toleranceMinus,
              tolerancePlus: sc.tolerancePlus,
              measurementId: mappedMeasurement.id ?? null,
              inspectionDataId: mappedMeasurement.inspectionDataId,
              measurement: mappedMeasurement.measurement,
              sampleDescription: mappedMeasurement.sampleDescription ?? null,
              sampleNumber,
              version: sc.version,
              createdOn: mappedMeasurement.createdOn,
              sizeOrder,
              dimensionOrder,
            });
          }
        });
      }
    }
  });

  const sortedResult: { [key: string]: SizeMeasurement[] } = {};

  Object.entries(unsortedResult)
    .sort((a, b) => {
      const aOrder = a[1][0]?.sizeOrder ?? 999;
      const bOrder = b[1][0]?.sizeOrder ?? 999;
      return aOrder - bOrder;
    })
    .forEach(([sizeKey, entries]) => {
      sortedResult[sizeKey] = entries
        .sort((a, b) => a.dimensionOrder - b.dimensionOrder)
        .map(({ sizeOrder, dimensionOrder, ...rest }) => rest);
    });

  return sortedResult;
};

export const DIMENSION_CHECK_MODULE_NAME = 'DimensionCheck';

export interface HasInspectionSizeChartParams {
  measurements: Measurement[];
  sizeCharts: SizeChart[] | undefined;
  productId: string | undefined;
}

export function hasInspectionSizeChart(params: HasInspectionSizeChartParams): boolean {
  const { measurements, sizeCharts, productId } = params;
  if (!productId) {
    return false;
  }
  return getSizeChartVersion(measurements ?? [], sizeCharts, productId).length > 0;
}

export function getAvailableInspectionTemplate(
  template: Template | null | undefined,
  params: HasInspectionSizeChartParams
): Template | null | undefined {
  if (!template) {
    return template;
  }
  if (hasInspectionSizeChart(params)) {
    return template;
  }
  return {
    ...template,
    modules: template.modules.filter((module) => module.name !== DIMENSION_CHECK_MODULE_NAME),
  };
}

function getSizeChartVersion(
  measurements: Measurement[],
  sizeChartData: SizeChart[] | undefined,
  productId: string
): SizeChart[] {
  if (!sizeChartData || !sizeChartData.length) return [] as SizeChart[];
  if (!measurements || !measurements.length) {
    let sizeChartByProduct = sizeChartData
      .filter((sc) => sc.productId === productId)
      .sort((a, b) => b.version - a.version);
    return sizeChartByProduct.filter((sc) => sc.version === sizeChartByProduct[0]?.version);
  }
  const matchedVersion = sizeChartData.find(
    (sc) => sc.id === measurements[0]?.sizeChartId
  )?.version;

  return sizeChartData.filter((sc) => sc.version === matchedVersion && sc.productId === productId);
}

const getInspectionFilterDates = (config: AppConfig): InspectionDateFilters => {
  const maxDays = Number(config?.inspections?.inspectionDateMax) || 5;
  const minDays = Number(config?.inspections?.inspectionDateMin) || 28;
  const fromDate = Number(config?.inspections?.conductedFromDate) || 1;
  const toDate = Number(config?.inspections?.conductedToDate) || 1;
  const now = new Date();
  const start = new Date();
  start.setDate(now.getDate() - fromDate);
  const end = new Date();
  end.setDate(now.getDate() + toDate);

  const min = new Date();
  min.setDate(now.getDate() - minDays);
  const max = new Date();
  max.setDate(now.getDate() + maxDays);
  return {
    start: start.toISOString().substring(0, 10),
    end: end.toISOString().substring(0, 10),
    min: min.toISOString().substring(0, 10),
    max: max.toISOString().substring(0, 10),
  };
};

interface GroupedInspection {
  controlPoint?: ControlPoint;
  product: Product | undefined;
  supplier: Supplier | undefined;
  factory?: Factory;
  externalPO: string | undefined;
  totalQuantity: number;
  inspectionDate: string;
  items: Array<GroupedInspectionItem>;
}

export function groupInspections<T extends Inspection>(
  items: T[] | undefined,
  getKeys: (item: T) => (string | undefined)[],
  getGroupData: (item: T) => {
    product: Product | undefined;
    supplier: Supplier | undefined;
    inspectionDate: string;
    controlPoint?: ControlPoint;
    lotSize: number;
    inspectionId: string;
    externalPO: string | undefined;
    factory?: Factory;
  }
): Record<string, GroupedInspection> {
  if (!items?.length) return {};

  return items.reduce(
    (acc, item) => {
      const keys = getKeys(item).filter(Boolean).join('-');
      if (!keys) {
        return acc;
      }

      if (!acc[keys]) {
        const { product, inspectionDate, controlPoint, supplier, externalPO, factory } =
          getGroupData(item);
        acc[keys] = {
          controlPoint,
          product,
          supplier,
          factory,
          totalQuantity: 0,
          inspectionDate,
          items: [],
          externalPO: externalPO,
        };
      }
      const group = acc[keys];
      const itemData = getGroupData(item);
      group.totalQuantity += itemData.lotSize;
      group.items.push({
        colours: item.colours || item.colourCodes,
        internalOrderReference: item.internalOrderReference,
        externalOrderReference: item.externalOrderReference,
        orderQuantity: item.orderQuantity,
        lotSize: item.lotSize,
        submissionNumber: item.submissionNumber,
        inspectionLevel: item.inspectionLevel,
        supplier: item.supplier,
        location: item.location,
        product: item.product,
        factory: item.factory,
        id: item.id,
        inspectionStatusId: item.inspectionStatusId,
        isFullDetailPreloaded: item.isFullDetailPreloaded,
        controlPoint: item.controlPoint,
      });
      return acc;
    },
    {} as Record<string, GroupedInspection>
  );
}

export const getTemplate = (templateId: string, version?: number): Template => {
  // const jsonData = await import(`@/data/inspection/${templateId}.json`);
  // return jsonData as Template;
  return structures.get(`${templateId?.toLowerCase()}_v${version || 1}`) as Template;
};

export function hasBundledTemplate(templateId: string, version: number = 1): boolean {
  return structures.has(`${templateId?.toLowerCase()}_v${version}`);
}

const getQuantityTolerance = (inspection: Inspection | undefined) => {
  return inspection?.quantityTolerancePlus && inspection?.quantityToleranceMinus
    ? `-${inspection.quantityToleranceMinus}/+${inspection.quantityTolerancePlus}%`
    : i18n.t('N/A');
};

const getProductDisplayText = (product?: Product): string => {
  return `${product?.code} - ${product?.name || ''}`;
};

const getDefectLanguage = (
  defect: DefectCode,
  locale: string | null | undefined = i18n.language
): DefectLanguage | null => {
  return selectDefectLanguage(defect?.languages, locale) ?? null;
};

export function displayedDefectField(
  defectCode:
    | {
        code?: string;
        codeDescription?: string;
        categoryDescription?: string;
        languages?: DefectLanguage[];
      }
    | null
    | undefined,
  field: 'codeDescription' | 'categoryDescription',
  locale: string | null | undefined = i18n.language
): string {
  const translated = selectDefectLanguage(defectCode?.languages, locale)?.[field]?.trim();
  if (translated) {
    return translated;
  }
  if (field === 'codeDescription') {
    return defectCode?.codeDescription?.trim() || defectCode?.code || '';
  }
  return defectCode?.categoryDescription?.trim() ?? '';
}

export function defectOptionSearchText(
  languages: DefectLanguage[] | null | undefined,
  field: 'codeDescription' | 'categoryDescription',
  locale: string | null | undefined = i18n.language
): string {
  return defectLabelSearchText(languages, locale, field);
}
const getDefectSeverity = (defect: InspectionDefect, defectSeverity: string) => {
  return defect.defectSeverity === defectSeverity ? defect.defectQuantity : '-';
};

const getInspectionModule = (moduleName: string): InspectionModuleComponentMap => {
  return inspectionModules[
    moduleName as keyof typeof inspectionModules
  ] as InspectionModuleComponentMap;
};

export const resetInspectionFilters = () => {
  localStorage.removeItem('inspectionProductCode');
  localStorage.removeItem('inspectionStartDate');
  localStorage.removeItem('inspectionEndDate');
  localStorage.removeItem('inspectionControlPoint');
};
function parseDefectCodesAndSeverity(
  response: ApiResponse<{ defects: Array<DefectCodes>; severities: Array<DefectSeverity> }>
) {
  const data = {
    defects: [] as Array<DefectCodes>,
    severities: [] as Array<DefectSeverity>,
  };

  if (response.data) {
    data.defects = response.data.defects;
    data.severities = response.data.severities;
  }

  // Process defect codes with language descriptions
  for (const category of data.defects) {
    if (category.codes?.length) {
      const categoryLanguage = getDefectLanguage(category.codes[0]);
      category.categoryDescription = categoryLanguage?.categoryDescription ?? '';
      for (const code of category.codes) {
        const codeLanguage = getDefectLanguage(code);
        code.categoryDescription =
          codeLanguage?.categoryDescription ?? category.categoryDescription;
        code.codeDescription = codeLanguage?.codeDescription?.trim()
          ? codeLanguage.codeDescription
          : code.code;
      }
    }
  }
  return data;
}

const getDivision = (inspection: Inspection, config: AppConfig, divisions: Division[]): string => {
  return (
    inspection.product?.productCategory?.department?.divisionId ||
    divisions?.find((d) => d.name === config[APP_CONFIG_KEY.defaultDivision])?.id ||
    ''
  );
};

export {
  getInspectionFilterDates,
  getProductDisplayText,
  getInspectionModule,
  getDefectLanguage,
  getDefectSeverity,
  getQuantityTolerance,
  restructureSizeChartData,
  getSizeChartVersion,
  parseDefectCodesAndSeverity,
  getDivision,
};
